"use client";
import React from "react";
import Image from "next/image";
import styles from "./expertise.module.css";
import scrollStyles from "./expertiseScroll.module.css";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

export type ExpertiseItem = {
  id: string;
  icon?: string;
  iconAlt?: string;
  title?: string;
  description?: string;
};

export type ExpertiseProps = {
  subtitle?: string;
  title?: string;
  intro?: string;
  items?: ExpertiseItem[];
};

const fallbackItems: ExpertiseItem[] = [
  {
    id: "agile",
    icon: "",
    iconAlt: "product delivery",
    title: "Agile Leadership & Product Delivery",
    description:
      "Spearheading teams within an Agile SCRUM framework, driving productivity gains and predictable delivery.",
  },
  {
    id: "engineering",
    icon: "",
    iconAlt: "code merge",
    title: "Software Engineering Management",
    description:
      "Building and leading distributed teams with measurable improvements in throughput and quality.",
  },
  {
    id: "product",
    icon: "",
    iconAlt: "product management",
    title: "Product Management",
    description:
      "Roadmaps, user stories, and release planning that align outcomes to business goals.",
  },
  {
    id: "applications",
    icon: "",
    iconAlt: "application development",
    title: "Web & Mobile Applications",
    description:
      "Shipping cross-platform apps and web experiences that improve engagement and efficiency.",
  },
];

const Expertise: React.FC<ExpertiseProps> = ({
  subtitle = "Expertise Snapshot",
  title = "Elevating Technology & Innovation with Passion and Precision",
  intro = "As a dedicated Technology Leader with over 15 years of experience specializing in full-stack development with a focus on front-end technologies. My career journey encompasses a blend of development, product and people management, where fostering talent and operational excellence stands at the forefront of my approach.",
  items = fallbackItems,
}) => {
  const [sectionRef, isVisible] = useScrollAnimation();

  return (
    <section 
      ref={sectionRef as React.RefObject<HTMLElement>}
      className={`mx-auto max-w-7xl text-slate-400 py-24 px-6 lg:px-8 ${isVisible ? scrollStyles.sectionAnimation : ""}`}
    >
      <div className={`mx-auto text-center md:max-w-4xl mb-16 ${isVisible ? scrollStyles.headerAnimation : ""}`}>
        <p
          className={`text-xl leading-7 font-semibold mb-3 ${styles.expertiseTitle}`}
        >
          {subtitle}
        </p>
        <h2 className={`font-assistant text-5xl text-slate-200 mb-6`}>
          {title}
        </h2>
        <p className={`text-lg leading-7 font-normal`}>{intro}</p>
      </div>
      <div
        className={`grid grid-cols-1 md:grid-cols-2 gap-x-20 md:gap-x-32 gap-y-20`}
      >
        {items.map((item, idx) => (
          <div className={`col-span-1 ${isVisible ? scrollStyles.cardAnimation : ""}`} key={item.id ?? idx}>
            {item.icon ? (
              item.icon.endsWith('.svg') || item.icon.includes('.svg') ? (
                // Use regular img tag for SVGs
                <img
                  src={item.icon}
                  alt={item.iconAlt ?? item.title ?? "icon"}
                  height={40}
                  width={40}
                  style={{ width: "auto" }}
                  className={`mb-4 pl-6`}
                />
              ) : (
                // Use Next.js Image for other formats
                <Image
                  src={item.icon}
                  alt={item.iconAlt ?? item.title ?? "icon"}
                  height={40}
                  width={40}
                  style={{ width: "auto" }}
                  className={`mb-4 pl-6`}
                  unoptimized={item.icon.endsWith('.svg') || item.icon.includes('.svg')}
                />
              )
            ) : null}
            <h3
              className={`mb-2 font-semibold text-slate-200 pl-6 border-l-2 ${styles.expertiseBorder}`}
            >
              {item.title}
            </h3>
            <p className={`font-normal pl-6`}>{item.description}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Expertise;
